//
//  HelpTopicFormDrawer.tsx
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module renders the help topic editor drawer.
//

import { Drawer } from "@mantine/core";
import { CheckboxInput, TextInput } from "@ngt-form/mantine";
import { useMemo, type FC } from "react";

import {
    Permission,
    type HelpTopicMetadataContext,
    type HelpTopicPermission
} from "src/api/Tmd.dtos";
import LongGroupedField from "src/components/forms/fields/grouped/LongGroupedField";
import ObjectArrayMultiSelectInput from "src/components/forms/inputs/ObjectArrayMultiSelectInput";
import ModelForm, { ModelFormLoader, useModelFormState } from "src/components/forms/ModelForm";
import useHelpTopicCreate from "src/hooks/data/create/useHelpTopicCreate";
import useHelpTopicFormMetadata from "src/hooks/data/form/metadata/useHelpTopicFormMetadata";
import useHelpTopicFormSubmit from "src/hooks/data/form/submit/useHelpTopicFormSubmit";
import useHelpTopicFormValidation from "src/hooks/data/form/validation/useHelpTopicFormValidation";
import useHelpTopicById from "src/hooks/data/load/useHelpTopicById";

import HelpTopicSectionsField from "./HelpTopicSectionsField";
import HelpTourStepsField from "./HelpTourStepsField";

/** Supplies the topic being edited, the page it belongs to and the host's callbacks. */
export interface HelpTopicFormDrawerProps {
    id?: number;
    routeKey: string;
    opened: boolean;
    onClose: () => void;
    onSaved: () => Promise<unknown> | void;
}

/** Supplies the help topic form its record, without the surrounding drawer chrome. */
type HelpTopicFormProps = Omit<HelpTopicFormDrawerProps, "opened">;

/**
 * Loads a help topic, or initializes one for the page, and hosts its embedded form.
 *
 * Postconditions: A falsy ID creates a topic whose page is the supplied route key. Supplying
 * onCancel keeps the form from navigating away from the page being described; a successful save
 * refreshes the host and closes the drawer.
 * Assumptions: Help topics are global, so the permission context is empty. The page field is
 * read-only because a topic's page is identified by where it was opened.
 */
const HelpTopicForm: FC<HelpTopicFormProps> = ({ id, routeKey, onClose, onSaved }) => {
    const context = useMemo(() => ({ routeKey }) as HelpTopicMetadataContext, [routeKey]);
    const { insert, dataState, metadataState, loading } = useModelFormState(
        id,
        context,
        useHelpTopicCreate,
        useHelpTopicById,
        useHelpTopicFormMetadata
    );

    if (loading) {
        return (
            <ModelFormLoader
                loading
                text="Loading help..."
            />
        );
    }

    return (
        <ModelForm
            insert={insert}
            dataState={dataState}
            metadataState={metadataState}
            saveHook={useHelpTopicFormSubmit}
            validateHook={useHelpTopicFormValidation}
            context={context}
            createPermission={Permission.HelpTopicsCreate}
            updatePermission={Permission.HelpTopicsUpdate}
            permissionContext={{}}
            showSubmitAndReturn={false}
            onCancel={onClose}
            afterSave={async () => {
                await onSaved();
                onClose();
            }}
        >
            <LongGroupedField
                propertyPath="routeKey"
                formInputComponent={TextInput}
                disabled
            />
            <LongGroupedField
                propertyPath="title"
                formInputComponent={TextInput}
                placeholder="Help"
            />
            <LongGroupedField
                propertyPath="active"
                formInputComponent={CheckboxInput}
            />
            <LongGroupedField
                propertyPath="requiredPermissions"
                formInputComponent={
                    ObjectArrayMultiSelectInput as typeof ObjectArrayMultiSelectInput<HelpTopicPermission>
                }
                valueProperty="permission"
                createItem={permission => ({ permission }) as HelpTopicPermission}
                description="Leave empty to list this page's help for everyone on the Help page. Otherwise it is listed only for users who hold any one of these permissions somewhere, or all of them when Require all is ticked. The help panel on the page itself is not affected."
                inputWrapperOrder={["label", "input", "description", "error"]}
                placeholder="Everyone"
                nothingFoundMessage="No results found..."
                clearable
                searchable
            />
            <LongGroupedField
                propertyPath="requireAllPermissions"
                formInputComponent={CheckboxInput}
            />
            <HelpTopicSectionsField />
            <HelpTourStepsField routeKey={routeKey} />
        </ModelForm>
    );
};

/**
 * Presents the help topic editor over the page.
 *
 * Postconditions: The form is mounted only while the drawer is open, so each open starts from
 * the saved topic rather than an abandoned draft.
 */
const HelpTopicFormDrawer: FC<HelpTopicFormDrawerProps> = ({ opened, ...props }) => (
    <Drawer
        opened={opened}
        onClose={props.onClose}
        position="right"
        size="xl"
        title={props.id ? "Edit Help" : "Add Help"}
        overlayProps={{ backgroundOpacity: 0.4, blur: 2 }}
        styles={{
            body: { padding: "var(--mantine-spacing-sm)" },
            header: {
                paddingLeft: "var(--mantine-spacing-sm)",
                paddingRight: "var(--mantine-spacing-sm)"
            },
            title: {
                color: "var(--mantine-color-primary-text)",
                fontSize: "var(--mantine-h2-font-size)",
                fontWeight: "var(--mantine-h2-font-weight)",
                letterSpacing: "var(--mantine-h2-letter-spacing)",
                lineHeight: "var(--mantine-h2-line-height)"
            }
        }}
    >
        {opened && <HelpTopicForm {...props} />}
    </Drawer>
);

export default HelpTopicFormDrawer;
